import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle, Loader2 } from 'lucide-react';
import { companyInfo } from '../mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`https://hk-altbausanierung-front-backend-lk.vercel.app/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Werden Sie unser nächstes
            <span className="block mt-2 text-[#DC2626]">Erfolgsprojekt</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6 md:space-y-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">Kontaktieren Sie uns</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 md:mb-8">
                Haben Sie Fragen zu Ihrem Sanierungsprojekt? Unser Team steht Ihnen gerne für eine persönliche Beratung zur Verfügung.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-5 md:space-y-6">
              <div className="flex items-start space-x-4 group">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#DC2626] to-[#B91C1C] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base md:text-lg mb-1">Adresse</h4>
                  <p className="text-gray-600 text-sm md:text-base">{companyInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#DC2626] to-[#B91C1C] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base md:text-lg mb-1">Telefon HK Altbausanierung</h4>
                  <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-[#DC2626] transition-colors block text-sm md:text-base">
                    {companyInfo.phone} (Mobil)
                  </a>
                  <a href={`tel:${companyInfo.phoneLandline}`} className="text-gray-600 hover:text-[#DC2626] transition-colors block text-sm md:text-base">
                    {companyInfo.phoneLandline} (Festnetz)
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#DC2626] to-[#B91C1C] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base md:text-lg mb-1">Telefon Bauleiter Günter Baumann</h4>
                  <a href="tel:+491727452138" className="text-gray-600 hover:text-[#DC2626] transition-colors block text-sm md:text-base">
                    0172 7452138
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 group">
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#DC2626] to-[#B91C1C] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-base md:text-lg mb-1">E-Mail</h4>
                  <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-[#DC2626] transition-colors text-sm md:text-base">
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Why HK Altbausanierung */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 md:p-8 rounded-2xl border border-gray-100 shadow-md">
              <h4 className="font-bold text-gray-900 text-lg md:text-xl mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-[#DC2626] mr-2" />
                Warum HK Altbausanierung?
              </h4>
              <ul className="space-y-3 text-gray-600 text-sm md:text-base">
                <li className="flex items-start">
                  <span className="text-[#DC2626] mr-2">✓</span>
                  Über 13 Jahre Erfahrung in der Altbausanierung
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC2626] mr-2">✓</span>
                  Staatlich geprüfte Qualifikationen
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC2626] mr-2">✓</span>
                  Persönliche Beratung und Betreuung
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC2626] mr-2">✓</span>
                  Kostenlose Erstbesichtigung
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Nachricht senden</h3>
            
            {submitStatus === 'success' ? (
              <div className="text-center py-8 md:py-12">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <CheckCircle className="w-8 h-8 md:w-10 md:h-10 text-green-600" />
                </div>
                <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Vielen Dank!</h4>
                <p className="text-gray-600 text-sm md:text-base">
                  Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns schnellstmöglich bei Ihnen.
                </p>
                <button
                  onClick={() => setSubmitStatus(null)}
                  className="mt-6 text-[#DC2626] font-semibold hover:underline"
                >
                  Neue Nachricht senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                    placeholder="Ihr Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                    placeholder="ihre@email.de"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                    placeholder="Ihre Telefonnummer"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-300 resize-none text-sm md:text-base"
                    placeholder="Beschreiben Sie Ihr Anliegen..."
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] text-white py-3 md:py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed text-sm md:text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Nachricht senden
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet und nicht gespeichert.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
